import { TypeKeys } from '../actions';
import { trackIntercomEvent } from '../global/intercom';
import SwWorkout from '@sworkit/workout';
import { swApi, jsVersionWithBuildNumber } from '../global/app';
import { IConfig, SwUI } from '@sworkit/workout/dist/types/supporting/types';
import { WorkoutState } from '@sworkit/workout/dist/types/supporting/workout-player-state';
import {
  endWatchWorkout,
  updateWatchState,
  initWatch,
} from '../global/apple-watch/apple-watch';
import { showSubscribeModal } from '../global/show-subscribe-modal';
import { logFirebaseAnalyticsEvent } from '../global/firebase-analytics';
import { showPreloadErrorModal } from '../global/preload-error-modal';
import { getDeviceType } from '../global/sw-device';
import {
  getChallengeIdsFromUser,
  getConnectedApps,
  getParentURL,
  waitFor,
} from '../global/utils';
import { AlertOptions, alertController } from '@ionic/core';
import { translate } from '../global/translate';
import { amplitudeLogEvent, AmplitudeEvents } from '../global/amplitude';
import { WorkoutLog, WorkoutObject, WorkoutSettings } from '@sworkit/api';
import { firebaseFunctions } from '../global/firebase';
import { markCollectionWorkoutComplete } from '../global/firestore-workouts-completed-collection';
import { addWorkoutLog } from '../global/firestore-workout-logs';
import {
  getGymPassIdFromUserDoc,
  logGympassAnalytics,
} from '../global/gympass';
import { Capacitor } from '@capacitor/core';
import SworkitMobile from '../global/sworkit-mobile/sw-mobile';
import { httpsCallable } from 'firebase/functions';
import { KeepAwake } from '@capacitor-community/keep-awake';
import workoutLogs from '../stencil-store/workout-logs.store';

declare var Ionic;
let sw: SwWorkout;

export const initSwWorkout =
  (collectionSlug?: string, workoutSlug?: string) =>
  async (dispatch, getState) => {
    const deviceType = await getDeviceType();

    const config: IConfig = {
      api: swApi,
      appVersion: jsVersionWithBuildNumber,
      platform: Capacitor.getPlatform() as 'ios' | 'android' | 'web',
      deviceType,
      connectedApps: getConnectedApps(getState()),
      preload: preloadFunctions(),
      videoType: 'ios',
      // debug: true
      // TODO:
    };
    sw = new SwWorkout(config);
    initWatch(sw);
    console.debug('initiated', sw);
    const events = {
      onWorkoutBegin: (sw: SwWorkout) => {
        const metadata = {
          workout_slug: sw.workout.slug,
          collection_slug: sw.workout.collectionSlug,
          category: sw.workout.category,
          total_time: sw.util.secondsToHms(sw.totalTime),
          url: getParentURL(),
        };
        trackIntercomEvent('workout_started', metadata);
        amplitudeLogEvent(AmplitudeEvents.workout_started, metadata);
        KeepAwake.keepAwake();
        swApi.analytics.workout.start(metadata.workout_slug, sw.totalTime);
        // initWatch(sw);
        const { user } = getState() as SworkitState;
        const gymPassId = getGymPassIdFromUserDoc(user);
        logGympassAnalytics(gymPassId, 'video');
      },
      onCancel: () => {
        swApi.analytics.workout.cancel(sw.workoutSlug);
      },
      onWorkoutEnd: (log: WorkoutLog) => {
        console.debug('Workout Ended', log);

        navigateToEndWorkout(log);
        KeepAwake.allowSleep();
        sendEndWorkoutAnalytics(log);
        if (
          log.minutesCompleted >= 1 &&
          !sw.hasWorkoutLogSaved &&
          !sw.workoutLogIsSaving
        ) {
          //only save log if at least 1 min
          const { user } = getState() as SworkitState;
          log.challengeIds = getChallengeIdsFromUser(user);
          sw.workoutLogIsSaving = true;
          addWorkoutLog(log, user, () => sw.registerWorkoutSaved());
          markCollectionWorkoutComplete(
            log.collectionSlug,
            log.slug,
            log.id,
            log.minutesCompleted,
          );
          saveLogBackup(swApi.uid, log);
        }
        dispatch(endWorkoutAction());
        endWatchWorkout(log);
      },
      onReady: (sw: SwWorkout) => {
        dispatch(workoutReady(sw));
      },
    };
    const ui: SwUI = {
      stateChanged: (state: WorkoutState) => {
        dispatch(updateWorkoutPlayer(state));
        updateWatchState(state);
      },
      playActiveVideo: () => {
        const videoEl = document.querySelector(
          '#workout-video',
        ) as HTMLVideoElement;
        videoEl ? videoEl.play() : null;
      },
      pauseActiveVideo: (time: number) => {
        const videoEl = document.querySelector(
          '#workout-video',
        ) as HTMLVideoElement;
        if (!videoEl) return;
        videoEl.pause();
        if (time) {
          const seconds = time / 1000;
          videoEl.currentTime = seconds;
        }
      },
      queueAndPlayAudio: (audio) => queueAndPlayAudio(audio),
      pauseAudio: () => pauseAudio(),
      resumeAudio: () => resumeAudio(),
      stopAudio: () => stopAudio(),
    };
    sw.setEvents(events);
    sw.setUI(ui);
    window['sw'] = sw;
    if (workoutSlug) {
      dispatch(loadWorkout(collectionSlug, workoutSlug));
    }

    const {
      ui: { online },
      user: { workoutSettings, profile },
    } = getState() as SworkitState;
    sw.online = online;
    if (workoutSettings) {
      if (typeof profile?.weightInLbs !== 'undefined') {
        workoutSettings.userWeightInLbs = profile?.weightInLbs;
      }
      updateWorkoutSettings(workoutSettings);
    }
  };

export interface UpdateWorkoutPlayer {
  type: TypeKeys.UPDATE_WORKOUT_PLAYER;
  workoutPlayerState: Partial<WorkoutPlayerState>;
}

export const dismissedLiveExplanation = () => async (dispatch, _getState) => {
  dispatch(updateWorkoutPlayer({ dismissedLive: true }));
};

const updateWorkoutPlayer = (
  workoutPlayerState: Partial<WorkoutPlayerState>,
) => {
  const action: UpdateWorkoutPlayer = {
    type: TypeKeys.UPDATE_WORKOUT_PLAYER,
    workoutPlayerState,
  };
  return action;
};

export const loadWorkout =
  (
    collectionSlug: string,
    workoutSlug: string,
    workoutObject?: WorkoutObject,
    workoutLength?: number,
  ) =>
  async (dispatch, getState) => {
    const {
      workoutPlayer: { workoutSlug: currentWorkoutSlug, hasWorkoutEnded },
    } = getState() as SworkitState;
    if (!collectionSlug || !workoutSlug) return;
    if (workoutSlug === currentWorkoutSlug && !hasWorkoutEnded) return;
    await dispatch(loadWorkoutAction(collectionSlug, workoutSlug));

    console.debug('load workout', workoutSlug, collectionSlug);
    if (workoutObject)
      return sw.loadWorkout(workoutObject, collectionSlug, workoutLength);
    return sw.loadWorkout(workoutSlug, collectionSlug, workoutLength);
  };

export interface LoadWorkout {
  type: TypeKeys.LOAD_WORKOUT;
  collectionSlug: string;
  workoutSlug: string;
}

const loadWorkoutAction = (collectionSlug: string, workoutSlug: string) => {
  const action: LoadWorkout = {
    type: TypeKeys.LOAD_WORKOUT,
    collectionSlug,
    workoutSlug,
  };
  return action;
};

export function sendEndWorkoutAnalytics(log: WorkoutLog) {
  if (log && log.minutesCompleted >= 2) {
    swApi.analytics.workout.end(log.slug, log.minutesCompleted);
    logFirebaseAnalyticsEvent('sw_fa_workout_end', {
      slug: log.slug,
      minutes_completed: log.minutesCompleted,
    });
  }
  amplitudeLogEvent(AmplitudeEvents.workout_ended, {
    workout_slug: log?.slug,
    collection_slug: log?.collectionSlug,
    category: sw?.workout?.category,
    minutes_completed: log?.minutesCompleted,
    type: log?.type,
    url: getParentURL(),
    id: log?.id,
  });
}

export const playWorkout = () => async (_dispatch, _getState) => {
  const {
    subscription: { canPlayWorkout },
    workoutPlayer: { preloadError },
  } = _getState() as SworkitState;
  if (!canPlayWorkout) {
    showSubscribeModal();
  } else if (preloadError) {
    showPreloadErrorModal();
  } else {
    sw.play();
  }
};

export interface PauseWorkout {
  type: TypeKeys.PAUSE_WORKOUT;
}
function pauseWorkoutAction() {
  const action: PauseWorkout = {
    type: TypeKeys.PAUSE_WORKOUT,
  };
  return action;
}

export const pauseWorkout = () => async (dispatch, _getState) => {
  if (sw && sw.isPlaying) {
    sw.pause();
  }
  return dispatch(pauseWorkoutAction());
};

export interface SkipExercise {
  type: TypeKeys.SKIP_EXERCISE;
}
export const skipExercise = () => async (dispatch, _getState) => {
  const action: SkipExercise = {
    type: TypeKeys.SKIP_EXERCISE,
  };
  await dispatch(action);
  sw.skip();
};

export interface PreviousExercise {
  type: TypeKeys.PREVIOUS_EXERCISE;
}
export const previousExercise = () => async (dispatch, _getState) => {
  sw.previous();
  const action: PreviousExercise = {
    type: TypeKeys.PREVIOUS_EXERCISE,
  };
  return dispatch(action);
};

export interface CancelWorkout {
  type: TypeKeys.CANCEL_WORKOUT;
}
export const cancelWorkout = () => async (dispatch, _getState) => {
  if (sw?.workout) {
    const workoutSlug = sw.workout?.slug;
    const collectionSlug = sw.workout?.collectionSlug;
    stopAudio();
    await sw.cancel();
    const action: CancelWorkout = {
      type: TypeKeys.CANCEL_WORKOUT,
    };
    await dispatch(action);
    sw = null;
    window['sw'] = null;
    dispatch(initSwWorkout(collectionSlug, workoutSlug));
  }
};

export const confirmEndWorkout = () => async (dispatch, _getState) => {
  dispatch(pauseWorkout());
  const alertOptions: AlertOptions = {
    header: translate('CONFIRM_END_WORKOUT'),
    message: translate('CONFIRM_END_WORKOUT_DESC'),
    buttons: [
      {
        text: translate('CANCEL'),
      },
      {
        text: translate('END_WORKOUT'),
        handler: () => {
          dispatch(endWorkout());
        },
      },
    ],
  };
  const alert = await alertController.create(alertOptions);
  alert.present();
};

export interface EndWorkout {
  type: TypeKeys.END_WORKOUT;
}
function endWorkoutAction() {
  const action: EndWorkout = {
    type: TypeKeys.END_WORKOUT,
  };
  return action;
}

export const endWorkout = () => async (_dispatch, _getState) => {
  return sw.end();
};

export interface PlayExerciseIndex {
  type: TypeKeys.PLAY_EXERCISE_INDEX;
}
export const playExerciseIndex =
  (index: number) => async (dispatch, _getState) => {
    sw.playExercise(index);
    const action: PlayExerciseIndex = {
      type: TypeKeys.PLAY_EXERCISE_INDEX,
    };
    return dispatch(action);
  };

export interface ToggleShowPlaylist {
  type: TypeKeys.TOGGLE_SHOW_PLAYLIST;
  showPlaylist: boolean;
}
export const toggleShowPlaylist =
  (showPlaylist: boolean) => async (dispatch, _getState) => {
    const action: ToggleShowPlaylist = {
      type: TypeKeys.TOGGLE_SHOW_PLAYLIST,
      showPlaylist,
    };
    return dispatch(action);
  };

export interface ToggleShowPreviewPanel {
  type: TypeKeys.TOGGLE_SHOW_PREVIEW_PANEL;
  showPreviewPanel: boolean;
}
export const toggleShowPreviewPanel =
  (showPreviewPanel: boolean) => async (dispatch, _getState) => {
    const action: ToggleShowPreviewPanel = {
      type: TypeKeys.TOGGLE_SHOW_PREVIEW_PANEL,
      showPreviewPanel,
    };
    return dispatch(action);
  };

export interface SwWorkoutReady {
  type: TypeKeys.SW_WORKOUT_READY;
  workoutPlayerState: Partial<WorkoutPlayerState>;
}

const workoutReady =
  (sw: SwWorkout, workoutLength?: number) => async (dispatch, _getState) => {
    if (workoutLength) {
      await dispatch(setWorkoutLength(workoutLength));
    }
    const action: SwWorkoutReady = {
      type: TypeKeys.SW_WORKOUT_READY,
      workoutPlayerState: {},
    };
    if (sw.workoutSettings.shouldPerformWarmUp) {
      action.workoutPlayerState.totalWorkoutTimerColor =
        'var(--color-sw-feature-2)';
    }
    dispatch(action);
    //clearly not everything is ready as this won't load everything without a timeout
    setTimeout(() => {
      sw.preloadAllWorkoutAssets();
    }, 333);
  };

export const navigateToEndWorkout = (
  log: Pick<
    WorkoutLog,
    'calories' | 'minutesCompleted' | 'collectionSlug' | 'slug' | 'name'
  >,
) => {
  return document.querySelector('ion-router-outlet').setRouteId(
    'sw-end-workout-page',
    {
      caloriesBurned: log.calories,
      minutesCompleted: log.minutesCompleted,
      collectionSlug: log.collectionSlug,
      workoutSlug: log.slug,
      name: log.name,
      workoutLog: log,
    },
    'forward',
  );
};

export const setWorkoutLength =
  (workoutLength: number) => async (_dispatch, getState) => {
    const {
      workoutPlayer: { workoutLength: currentWorkoutLength },
    } = getState() as SworkitState;
    if (workoutLength !== currentWorkoutLength) {
      sw.setWorkoutLength(workoutLength);
    }
  };

export const updateWorkoutSettings = (workoutSettings: WorkoutSettings) => {
  if (!workoutSettings || !sw) return;
  if (sw.isReady) {
    console.debug(':::updateWorkoutSettings', workoutSettings);
    sw.updateWorkoutSettings(workoutSettings);
  } else {
    console.debug(':::setWorkoutSettings', workoutSettings);
    sw.setWorkoutSettings(workoutSettings, swApi.language);
  }
};

export const queueAndPlayAudio = (audio: string | string[]) => {
  console.debug('queue and play audio', audio);
  const nativeAudio = Array.isArray(audio) ? { urls: audio } : { url: audio };
  if (Capacitor.isNativePlatform()) {
    return SworkitMobile.playAudio(nativeAudio);
  } else {
    return document.querySelector('sw-audio').playAudio(audio);
  }
};

const preloadFunctions = () => {
  if (!Capacitor.isNativePlatform()) return null;
  return {
    audio: async (url: string) => {
      const response: any = await SworkitMobile.preloadAudio({
        paths: [url],
      });
      const arr = JSON.parse(response.paths);
      const path = arr[0];
      if (path) {
        return path;
      } else {
        return sw.preloadWebAsset(url);
      }
    },
    video: async (url: string) => {
      const response: any = await SworkitMobile.preloadVideo({
        paths: [url],
      });
      const arr = JSON.parse(response.paths);
      const path = arr[0];
      if (path) {
        return Ionic.WebView.convertFileSrc(path);
      } else {
        return sw.preloadWebAsset(url);
      }
    },
    image: async (url: string) => {
      const response: any = await SworkitMobile.preloadImage({
        paths: [url],
      });
      const arr = JSON.parse(response.paths);
      const path = arr[0];
      if (path) {
        return Ionic.WebView.convertFileSrc(path);
      } else {
        return sw.preloadWebAsset(url);
      }
    },
  };
};

export function togglePlayerOnlineStatus(connected: boolean) {
  if (swApi && swApi.restfire) {
    swApi.restfire.toggleOnline(connected);
  }
  if (sw) {
    sw.toggleOnline(connected);
  }
}

const saveLogBackup =
 async  (uid: string, log: WorkoutLog) =>  {
    // only include workouts that are at least 1 min
    await waitFor(10000);
    // timeout 10 seconds
    console.debug('checking for saved workout log');
   
    if (!workoutLogs.recent?.length) return;
    // is this workout in the recents?
    const workoutLog = workoutLogs.recent.find(
      (workout) =>
        workout.collectionSlug === log.collectionSlug &&
        workout.slug === log.slug &&
        workout.minutesCompleted === log.minutesCompleted &&
        workout.calories === log.calories,
    );

    if (workoutLog)
      return console.debug('log found! already saved successfully', workoutLog);
    // if workout log isn't saved yet - call Cloud Function
    console.debug('saving log because its not here after 10 seconds', uid, log);
    try {
      const res = await httpsCallable<
        { uid: string; log: WorkoutLog },
        { workoutLogSaved: boolean }
      >(
        firebaseFunctions,
        'saveWorkoutLogBackup',
      )({
        uid,
        log,
      });
      if (res?.data?.workoutLogSaved) {
        // add amplitude event.
        amplitudeLogEvent(AmplitudeEvents.saved_workout_log_via_cf);
        console.debug('workout log saved via cloud function', log);
      } else {
        console.debug('workout already log saved', log);
      }
    } catch (err) {
      console.error('error sending log to firestore');
    }
  };

function pauseAudio() {
  // console.debug('pause audio');
  if (Capacitor.isNativePlatform()) {
    SworkitMobile.pauseAudio();
  } else {
    document.querySelector('sw-audio').pauseAudio();
  }
}

function resumeAudio() {
  // console.debug('resume audio');
  if (Capacitor.isNativePlatform()) {
    SworkitMobile.resumeAudio();
  } else {
    document.querySelector('sw-audio').resumeAudio();
  }
}

export function stopAudio() {
  // console.debug('stop audio');
  if (Capacitor.isNativePlatform()) {
    SworkitMobile.stopAudio();
  } else {
    pauseAudio();
    document.querySelector('sw-audio').clearAudio();
  }
}

export function preloadAssets() {
  if (sw && sw.workout && sw.preloadAssets) {
    return sw.preloadAssets();
  }
}

export function setActiveWorkoutLogNote(note: string) {
  if (!sw || !sw.workout || !sw.workout.log) return;
  sw.workout.log.note = note;
}

export function getActiveWorkoutLogNote() {
  if (!sw || !sw.workout || !sw.workout.log) return null;
  return sw.workout.log.note;
}

export function isLargeScreen() {
  return window.innerWidth >= 576 && window.innerHeight >= 576;
}
